import {
  ArrowLeftRight,
  ArrowRight,
  CalendarDays,
  Camera,
  Check,
  ChevronUp,
  CircleCheck,
  CircleDot,
  ClipboardCheck,
  Copy,
  Database,
  Download,
  ExternalLink,
  FileText,
  Fingerprint,
  Folder,
  House,
  Image as ImageIcon,
  KeyRound,
  LayoutGrid,
  LockKeyhole,
  LogOut,
  Notebook,
  PanelLeft,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserRound,
  X,
  createElement,
  type IconNode,
} from "lucide";

export type UiIconName =
  | "arrow-left-right"
  | "arrow-right"
  | "calendar-days"
  | "camera"
  | "check"
  | "chevron-up"
  | "circle-check"
  | "circle-dot"
  | "clipboard-check"
  | "copy"
  | "database"
  | "download"
  | "external-link"
  | "file-text"
  | "fingerprint"
  | "folder"
  | "house"
  | "image"
  | "key-round"
  | "layout-grid"
  | "lock-keyhole"
  | "log-out"
  | "notebook"
  | "panel-left"
  | "pencil"
  | "plus"
  | "refresh-cw"
  | "search"
  | "shield-check"
  | "sparkles"
  | "trash"
  | "user-round"
  | "x";

const ICONS: Record<UiIconName, IconNode> = {
  "arrow-left-right": ArrowLeftRight,
  "arrow-right": ArrowRight,
  "calendar-days": CalendarDays,
  camera: Camera,
  check: Check,
  "chevron-up": ChevronUp,
  "circle-check": CircleCheck,
  "circle-dot": CircleDot,
  "clipboard-check": ClipboardCheck,
  copy: Copy,
  database: Database,
  download: Download,
  "external-link": ExternalLink,
  "file-text": FileText,
  fingerprint: Fingerprint,
  folder: Folder,
  house: House,
  image: ImageIcon,
  "key-round": KeyRound,
  "layout-grid": LayoutGrid,
  "lock-keyhole": LockKeyhole,
  "log-out": LogOut,
  notebook: Notebook,
  "panel-left": PanelLeft,
  pencil: Pencil,
  plus: Plus,
  "refresh-cw": RefreshCw,
  search: Search,
  "shield-check": ShieldCheck,
  sparkles: Sparkles,
  trash: Trash2,
  "user-round": UserRound,
  x: X,
};

export interface IdentityView {
  handle: string;
  displayName: string;
  rootDid: string;
  avatarDataUri?: string | null;
}

export function icon(
  name: UiIconName,
  options: { size?: number; label?: string; className?: string } = {},
): string {
  const label = options.label?.trim();
  const element = createElement(ICONS[name], {
    width: options.size ?? 18,
    height: options.size ?? 18,
    class: `ui-icon${options.className ? ` ${options.className}` : ""}`,
    focusable: "false",
    ...(label
      ? { role: "img", "aria-label": label }
      : { "aria-hidden": "true" }),
  });
  return element.outerHTML;
}

export function identicon(value: string, size = 32, label?: string): string {
  const cells = identiconCells(value);
  const hue = Math.abs(hashString(`${value}:color`)) % 360;
  const title = label ? `<title>${escapeHtml(label)}</title>` : "";
  const accessibility = label
    ? `role="img" aria-label="${escapeHtml(label)}"`
    : `aria-hidden="true"`;
  return `<svg class="ui-identicon" ${accessibility} focusable="false" width="${size}" height="${size}" viewBox="0 0 5 5" xmlns="http://www.w3.org/2000/svg">${title}<rect width="5" height="5" rx="1" fill="hsl(${hue} 64% 94%)"/>${cells.map(([x, y]) => `<rect x="${x}" y="${y}" width="1" height="1" fill="hsl(${hue} 58% 42%)"/>`).join("")}</svg>`;
}

export function avatar(profile: IdentityView, size: "tiny" | "small" | "large" = "small"): string {
  const image = profile.avatarDataUri
    ? `<img src="${escapeHtml(profile.avatarDataUri)}" alt="" />`
    : identicon(profile.rootDid, size === "large" ? 88 : size === "small" ? 36 : 29);
  return `<span class="ui-avatar ${size}" aria-hidden="true">${image}</span>`;
}

export function identitySwitcherTemplate(options: {
  profile: IdentityView;
  open: boolean;
  busy?: boolean;
  remembered?: IdentityView[];
  menuLabel: string;
  switchTitle?: string;
  switchDescription?: string;
  logoutTitle?: string;
  logoutDescription?: string;
  profileHref?: string;
  externalHref?: string;
  externalLabel?: string;
  privacyCopy?: string;
  fallbackHtml?: string;
}): string {
  const profile = options.profile;
  const remembered = options.remembered ?? [];
  const busy = options.busy ? "disabled" : "";
  return `<div class="ui-identity-switcher">
    <button class="ui-identity-trigger" data-ui-identity-trigger type="button" aria-label="${escapeHtml(options.menuLabel)}" aria-haspopup="menu" aria-expanded="${options.open}">
      ${avatar(profile)}
      <span class="ui-identity-trigger-copy"><strong>${escapeHtml(profile.displayName)}</strong><span>@${escapeHtml(profile.handle)}</span></span>
      <span class="ui-identity-chevron">${icon("chevron-up", { size: 16 })}</span>
    </button>
    ${options.open ? `<div class="ui-identity-menu" role="menu" aria-label="${escapeHtml(options.menuLabel)}">
      <div class="ui-identity-current">${avatar(profile)}<div><small>Current identity</small><strong>${escapeHtml(profile.displayName)}</strong><span>@${escapeHtml(profile.handle)}</span></div>${icon("circle-check", { size: 17, label: "Current identity" })}</div>
      <div class="ui-identity-did"><span>${identicon(profile.rootDid, 22, "DID identicon")}</span><div><small>DID identity cue</small><code>${escapeHtml(shortDid(profile.rootDid))}</code></div>${icon("fingerprint", { size: 16 })}</div>
      ${options.profileHref ? `<a class="ui-identity-profile-link" href="${escapeHtml(options.profileHref)}">${icon("user-round", { size: 17 })}<span><strong>View profile</strong><small>Photo, cover, and public identity</small></span>${icon("arrow-right", { size: 15 })}</a>` : ""}
      ${remembered.length ? `<div class="ui-remembered-identities"><small>Used on this device</small>${remembered.map((account) => `<button data-ui-remembered-identity data-login-hint="${escapeHtml(account.handle)}" data-root-did="${escapeHtml(account.rootDid)}" role="menuitem" type="button" ${busy}>${avatar(account, "tiny")}<span><strong>${escapeHtml(account.displayName)}</strong><small>@${escapeHtml(account.handle)}</small></span></button>`).join("")}</div>` : ""}
      <div class="ui-identity-actions">
        <button data-ui-switch-identity role="menuitem" type="button" ${busy}>${icon("arrow-left-right", { size: 18 })}<span><strong>${escapeHtml(options.switchTitle ?? "Switch identity")}</strong><small>${escapeHtml(options.switchDescription ?? "Sign in or create another identity")}</small></span></button>
        <button data-ui-logout class="danger" role="menuitem" type="button" ${busy}>${icon("log-out", { size: 18 })}<span><strong>${escapeHtml(options.logoutTitle ?? "Log out")}</strong><small>${escapeHtml(options.logoutDescription ?? "End this browser session")}</small></span></button>
      </div>
      ${options.fallbackHtml ?? ""}
      ${options.privacyCopy ? `<p class="ui-identity-privacy">${escapeHtml(options.privacyCopy)}</p>` : ""}
      ${options.externalHref ? `<a class="ui-identity-external" href="${escapeHtml(options.externalHref)}" target="_blank" rel="noopener noreferrer">${escapeHtml(options.externalLabel ?? "Open Vibe Cloud")}${icon("external-link", { size: 14 })}</a>` : ""}
    </div>` : ""}
  </div>`;
}

export function dashboardShellTemplate(options: {
  className: string;
  sidebar: string;
  main: string;
  includeModalRoot?: boolean;
}): string {
  return `<div class="app-shell ui-dashboard-shell ${escapeHtml(options.className)}">${options.sidebar}<div class="ui-dashboard-main">${options.main}</div>${options.includeModalRoot ? `<div id="modal-root"></div>` : ""}</div>`;
}

export function guidedStepsTemplate(
  steps: Array<{ label: string; state: "complete" | "current" | "upcoming" }>,
): string {
  return `<ol class="ui-guided-steps" aria-label="Setup progress">${steps.map((step, index) => `<li class="${step.state}" ${step.state === "current" ? `aria-current="step"` : ""}><span>${step.state === "complete" ? icon("check", { size: 14 }) : index + 1}</span><strong>${escapeHtml(step.label)}</strong></li>`).join("")}</ol>`;
}

export async function normalizeProfileImage(file: File, kind: "avatar" | "cover"): Promise<string> {
  if (!file.type.startsWith("image/")) throw new TypeError("Choose an image file.");
  if (file.size > 12 * 1024 * 1024) throw new TypeError("Choose an image smaller than 12 MB.");
  const source = await decodeImage(file);
  try {
    const ratio = kind === "avatar" ? 1 : 2.5;
    const maxWidth = kind === "avatar" ? 420 : 1400;
    const maxBytes = kind === "avatar" ? 128 * 1024 : 512 * 1024;
    const sourceRatio = source.width / source.height;
    const sourceWidth = sourceRatio > ratio ? source.height * ratio : source.width;
    const sourceHeight = sourceRatio > ratio ? source.height : source.width / ratio;
    const sourceX = (source.width - sourceWidth) / 2;
    const sourceY = (source.height - sourceHeight) / 2;
    for (const scale of [1, 0.82, 0.68, 0.54]) {
      const width = Math.max(160, Math.round(Math.min(maxWidth, sourceWidth) * scale));
      const height = Math.max(64, Math.round(width / ratio));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { alpha: false });
      if (!context) throw new Error("This browser cannot prepare the image.");
      context.fillStyle = "#f4f2fa";
      context.fillRect(0, 0, width, height);
      context.drawImage(source.image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, width, height);
      for (const quality of [0.84, 0.72, 0.6]) {
        const dataUri = canvas.toDataURL("image/jpeg", quality);
        if (dataUriBytes(dataUri) <= maxBytes) return dataUri;
      }
    }
    throw new TypeError(`The ${kind} image could not be reduced enough. Choose a simpler image.`);
  } finally {
    source.close();
  }
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/gu, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[character]!);
}

function identiconCells(value: string): Array<[number, number]> {
  const cells: Array<[number, number]> = [];
  let hash = hashString(value);
  for (let y = 0; y < 5; y += 1) {
    for (let x = 0; x < 3; x += 1) {
      hash = Math.imul(hash ^ (hash >>> 15), 2246822519);
      if ((hash & 1) === 0) continue;
      cells.push([x, y]);
      if (x !== 2) cells.push([4 - x, y]);
    }
  }
  return cells.length ? cells : [[2, 2]];
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash | 0;
}

function shortDid(did: string): string {
  return did.length > 28 ? `${did.slice(0, 18)}…${did.slice(-8)}` : did;
}

function dataUriBytes(dataUri: string): number {
  const comma = dataUri.indexOf(",");
  return comma === -1 ? Number.POSITIVE_INFINITY : Math.floor((dataUri.length - comma - 1) * 0.75);
}

async function decodeImage(file: File): Promise<{
  image: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}> {
  if ("createImageBitmap" in window) {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    return { image: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
  }
  const url = URL.createObjectURL(file);
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new TypeError("The selected image could not be read."));
      image.src = url;
    });
    return { image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}
